import type { IframePoolItem } from '../../../modules/api';
import { formatPrice } from '../../../modules/currency';
import { Image, Modal, SheetFrame } from '../../../modules/design-system';
import { SleekButton } from '../../../modules/theme';
import { useIsWideViewport } from './useIsWideViewport';

type Props = {
  item: IframePoolItem;
  onClose: () => void;
};

const TITLE_ID = 'whats-inside-sheet-title';
const SHEET_TITLE = "What's Inside";

/** The slab's box and the CDN size it is fetched at, per frame. */
const SLAB = {
  sheet: { className: 'h-[min(356px,40vh)]', width: 240, height: 356 },
  modal: { className: 'h-[min(480px,55vh)]', width: 324, height: 480 },
} as const;

/**
 * One "What's inside" card (partner feedback 2026-09-30: "match the odds
 * sheet"): the slab, its name and the estimated value, then Close. A bottom
 * sheet on a phone; on a desktop-wide screen a centred modal with a bigger
 * slab, as a phone sheet pinned to the bottom of a monitor read poorly.
 */
export function WhatsInsideSheet({ item, onClose }: Props) {
  const isWide = useIsWideViewport();

  if (isWide) {
    return (
      <Modal onClose={onClose} inspectName="WhatsInsideSheet" ariaLabelledBy={TITLE_ID} widthClassName="max-w-[480px]">
        <div className="bg-[var(--co-WhatsInsideSheet-background,#0E0F11)] p-6 [border-radius:var(--co-WhatsInsideSheet-borderRadius,24px)]">
          <CardDetails item={item} slab={SLAB.modal} onClose={onClose} />
        </div>
      </Modal>
    );
  }

  return (
    <SheetFrame
      onClose={onClose}
      ariaLabelledBy={TITLE_ID}
      inspectName="WhatsInsideSheet"
      panelClassName="bg-[var(--co-WhatsInsideSheet-background,#0E0F11)] [border-top-left-radius:var(--co-WhatsInsideSheet-borderRadius,24px)] [border-top-right-radius:var(--co-WhatsInsideSheet-borderRadius,24px)]"
      handleClassName="bg-[var(--co-WhatsInsideSheet-handle,#2F3037)]"
    >
      <CardDetails item={item} slab={SLAB.sheet} onClose={onClose} />
    </SheetFrame>
  );
}

type CardDetailsProps = {
  item: IframePoolItem;
  slab: (typeof SLAB)[keyof typeof SLAB];
  onClose: () => void;
};

function CardDetails({ item, slab, onClose }: CardDetailsProps) {
  const title = item.title || SHEET_TITLE;
  const priceLabel = formatPrice(item.itemPrice, 2);

  return (
    <div className="flex flex-col items-center gap-6 text-center">
      {/* Names the dialog; the Figma sheet opens straight on the card. */}
      <h2 id={TITLE_ID} className="sr-only">
        {SHEET_TITLE}
      </h2>
      <Image
        src={item.imageUrl}
        alt={title}
        className={`relative w-full ${slab.className}`}
        imgClassName="absolute inset-0 h-full w-full object-contain"
        width={slab.width}
        height={slab.height}
        quality={95}
      />
      <p className="text-[16px] font-medium leading-[20px] tracking-[-0.31px] text-[var(--co-WhatsInsideSheet-text,#FFFFFF)]">{title}</p>
      {priceLabel && (
        <div className="flex flex-col items-center gap-1">
          <span className="text-[44px] font-semibold leading-[52px] tracking-[0.37px] text-[var(--co-WhatsInsideSheet-text,#FFFFFF)]">
            {priceLabel}
          </span>
          <span className="text-[14px] font-medium leading-[20px] tracking-[-0.15px] text-[var(--co-WhatsInsideSheet-mutedText,#6D707C)]">
            Estimated Value
          </span>
        </div>
      )}
      <SleekButton name="WhatsInsideCloseButton" onClick={onClose} className="w-full">
        Close
      </SleekButton>
    </div>
  );
}
